import React, { useRef } from 'react';

import { Bins } from './bins';
import { Brush } from './brush';
import { XAxis } from './x-axis';
import { XAxisTitle } from './x-axis-title';
import { YAxis } from './y-axis';
// import { BinLines } from './bin-lines';

import { Observable } from '@reactivex/rxjs';
import shallowEqual from 'react-recompose/shallowEqual';
import mapPropsStream from 'react-recompose/mapPropsStream';
import { ScaleLinear, ScaleTime } from 'd3-scale';

import { withValueScale } from '../with-timebar-scales';
import { withBrushFilters } from '../with-brush-filters';
import { withBinHighlights } from '../with-bin-highlights';

import { BinDataContainer, BinData, Timebar } from './types';

const withUniqueClipPathIds = (staticClipPathId =>
    mapPropsStream(propsStream => {
        const clipPathId = `clip-${++staticClipPathId}`;
        const clipPathURL = `url(#clip-${staticClipPathId})`;
        return Observable.from(propsStream)
            .map((props: object) => ({
                ...props,
                clipPathId,
                clipPathURL
            }))
            .distinctUntilChanged(shallowEqual);
    }))(-1);

export interface TimebarProps {
    details?: BinDataContainer;
    summary?: BinDataContainer;
    timebar: Timebar;
    width: number;
    height: number;
    range?: number[];
    detailsLevel: number;
    minTime: number;
    maxTime: number;
    timeScale: ScaleTime<number, number>;
    zoomScale: ScaleTime<number, number>;
    valueScale: ScaleLinear<number, number>;
    zoomDomain: [number, number];
    timeDomain: [number, number];
    clipPathId: string;
    clipPathURL: string;
    yAxisWidth?: number;
    xAxisHeight?: number;
    isPanZooming: boolean;
    isPanScrolling: boolean;
    isWheelScrolling: boolean;
    onTouchExit?: (e: React.TouchEvent | React.MouseEvent) => void;
    onBrushTimeChange?: (v: {props: object, type: string, timeDomain: [number, number]}) => void;
    onScrollTouchStart?: (e: React.TouchEvent | React.MouseEvent) => void;
}
export const TimebarChart = withBinHighlights(withBrushFilters(withUniqueClipPathIds((props: TimebarProps) => {
    const {
        details = {}, summary = {}, timebar,
        width, height,
        range = [],
        detailsLevel,
        minTime, maxTime,
        valueScale, timeScale, zoomScale,
        timeDomain, zoomDomain,
        clipPathId, clipPathURL,
        yAxisWidth = 25, xAxisHeight = 30,
        isPanZooming, isPanScrolling, isWheelScrolling,
        onTouchExit, onBrushTimeChange,
        onScrollTouchStart
    } = props;

    if (!timeScale || !zoomScale) {
        return null;
    }

    const chartWidth = Math.max(0, Math.max(width - yAxisWidth - 10 || 0, 0));
    const summaryHeight = Math.min(Math.max(0, Math.max(height - xAxisHeight || 0, 0) * 0.3), 100);
    const summaryOn = summaryHeight > 50;
    const detailsHeight = summaryOn ? Math.max(0, Math.max(height || 0, 0) - summaryHeight) : height;

    const detailsGlobal = details?.global ?? {};
    const detailsMasked = details?.masked ?? {};
    const detailsFiltered = details?.filtered ?? {};
    const summaryGlobal = summary?.global ?? {};
    const summaryMasked = summary?.masked ?? {};
    const summaryFiltered = summary?.filtered ?? {};

    const { min: yDomainMin, max: yDomainMax } = (
        detailsMasked && detailsMasked.min != null ? detailsMasked
            : detailsFiltered && detailsFiltered.min != null ? detailsFiltered
                : detailsGlobal
    );

    let minVisibleCount = Number.POSITIVE_INFINITY;
    let maxVisibleCount = Number.NEGATIVE_INFINITY;
    const [minVisibleTime, maxVisibleTime] = zoomDomain.map((x) => +x);
    const detailsInOrder: BinData[] = [detailsGlobal || {}, detailsFiltered || {}, detailsMasked || {}];

    for (let detailsToTry;
        (minVisibleCount === Number.POSITIVE_INFINITY) &&
        (maxVisibleCount === Number.NEGATIVE_INFINITY) &&
        (detailsToTry = detailsInOrder.pop());) {
        const { binValues: visibleBinValues, binCounts: visibleBinCounts } = detailsToTry;
        if (visibleBinCounts && visibleBinValues) {
            let visibleBinId = findBinIndex(minVisibleTime, visibleBinValues) - 1;
            let lastVisibleBinId = findBinIndex(maxVisibleTime, visibleBinValues) + 1;
            while (++visibleBinId < lastVisibleBinId) {
                const count = visibleBinCounts[visibleBinId] || 0;
                if (count <= 0) continue;
                const t0 = +visibleBinValues[visibleBinId * 2];
                const t1 = +visibleBinValues[visibleBinId * 2 + 1];
                if (t1 < minVisibleTime || t0 > maxVisibleTime) {
                    continue;
                }
                (count < minVisibleCount) && (minVisibleCount = count);
                (count > maxVisibleCount) && (maxVisibleCount = count);
            }
        }
    }

    minVisibleCount === Number.POSITIVE_INFINITY && (minVisibleCount = yDomainMin);
    maxVisibleCount === Number.NEGATIVE_INFINITY && (maxVisibleCount = yDomainMax);

    return (
        <svg
            width={Math.max(0, width + 20)}
            height={Math.max(0, height)}
            onMouseLeave={onTouchExit}
            onMouseDown={onScrollTouchStart}
            onTouchStart={onScrollTouchStart}>
            <defs>
                <clipPath id={clipPathId}>
                    <rect x="0" y="0" width={chartWidth} height={detailsHeight} />
                </clipPath>
                <clipPath id={`${clipPathId}-filters`}>
                    <rect x="0" y="0" width={chartWidth + 10} height={detailsHeight + 10} />
                </clipPath>
            </defs>

            {/* The bottom summary chart (the minimap thing) */}
            {summaryOn ? <TimeBarSummary
                {...props}
                minTime={minTime}
                maxTime={maxTime}
                timeDomain={timeDomain}
                id="summary"
                x={yAxisWidth}
                yAxisWidth={0}
                y={height - summaryHeight}
                width={chartWidth}
                height={summaryHeight}
                isPanZooming={isPanZooming}
                isPanScrolling={isPanScrolling}
                onTouchExit={onTouchExit}
                onBrushTimeChange={onBrushTimeChange}
                min={summaryGlobal.min}
                max={summaryGlobal.max}
                numBins={summary.numBins}
                binCounts={summaryGlobal.binCounts}
                binValues={summaryGlobal.binValues}
                maskedCounts={summaryMasked.binCounts}
                maskedValues={summaryMasked.binValues}
                filteredCounts={summaryFiltered.binCounts}
                filteredValues={summaryFiltered.binValues}
                valueScale={valueScale}
                timeScale={timeScale.range([1, chartWidth - 1])}
            /> : null}

            {/* The main details chart (zoomed view) -- listed last so SVG places it (and its children) on top of everything else */}
            <TimeBarDetails
                {...props}
                id="details"
                x={0} y={0}
                width={chartWidth}
                height={detailsHeight}
                yAxisWidth={yAxisWidth}
                xAxisHeight={xAxisHeight}
                clipPathId={clipPathId}
                clipPathURL={clipPathURL}
                isPanZooming={isPanZooming}
                isPanScrolling={isPanScrolling}
                isWheelScrolling={isWheelScrolling}
                filterBrushRanges={range}
                min={minVisibleCount}
                max={maxVisibleCount}
                minTime={minTime}
                maxTime={maxTime}
                numBins={details.numBins}
                detailsLevel={detailsLevel}
                binCounts={detailsGlobal.binCounts}
                binValues={detailsGlobal.binValues}
                maskedCounts={detailsMasked.binCounts}
                maskedValues={detailsMasked.binValues}
                filteredCounts={detailsFiltered.binCounts}
                filteredValues={detailsFiltered.binValues}
                zoomDomain={zoomDomain}
                zoomScale={zoomScale.range([1, chartWidth - 1])}
                valueScale={valueScale}
                timebar={timebar}
            />

        </svg>
    );
}))) as React.ComponentClass<TimebarProps>;

export interface TimebarDetailsProps {
    [x: string]: any;
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    filterBrushRanges?: number[];
    minTime: number;
    maxTime: number;
    zoomScale: ScaleTime<number, number>;
    valueScale: ScaleLinear<number, number>;
    binValues: number[];
    zoomDomain: [number, number];
    clipPathId: string;
    clipPathURL: string;
    yAxisWidth: number;
    xAxisHeight: number;
    isWheelScrolling: boolean;
    detailsLevel: number;
    timebar: {mode: boolean};
    onTouchExit?: (e: React.TouchEvent | React.MouseEvent) => void;
    onBinTouchMove?: (e: React.TouchEvent | React.MouseEvent) => void;
    onBrushFilterStart?: (e: React.TouchEvent | React.MouseEvent) => void;
    onUpdateTimebarPlayheadStart?: (input: {value: number}) => void;
    onScrollTouchStart?: (e: React.TouchEvent | React.MouseEvent) => void;
    onFilterBrushTimeChange?: (v: {props: any, type: string; timeDomain: [number, number] }) => void;
    onFilterBrushRemoved?: (v: {props: any}) => void;
    onBinWheel?: (e: React.TouchEvent | React.MouseEvent) => void;
}
const TimeBarDetails = withBinHighlights(withValueScale((props: TimebarDetailsProps) => {
    const {
        filterBrushRanges = [],
        x, y, id, width, height,
        isWheelScrolling,
        yAxisWidth, xAxisHeight, clipPathId, clipPathURL,
        zoomScale, zoomDomain, valueScale,
        minTime, maxTime, binValues,
        onTouchExit, onBinTouchMove, onBrushFilterStart, onUpdateTimebarPlayheadStart,
        onFilterBrushTimeChange, onFilterBrushRemoved, onBinWheel,
        detailsLevel, timebar,
    } = props;

    const TimebarBg = useRef(null);

    if (!valueScale) { return null; }

    const mainChartAreaHeight = Math.max(0, height - 2 * xAxisHeight);
    const yAxisScale = valueScale.range([mainChartAreaHeight, 0]);

    const heightPadding =
        mainChartAreaHeight > 30 ? 10
            : mainChartAreaHeight > 10 ? 5
                : 0;

    const onTap = (t: number) => timebar.mode
        && onUpdateTimebarPlayheadStart({value: binValues[findBinIndex(t, binValues) * 2]});
    const onClickBg = (e: React.TouchEvent | React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e as React.TouchEvent).touches
            ? (e as React.TouchEvent).touches[0].clientX
            : (e as React.MouseEvent).clientX - rect.left;
        const t = +zoomScale.invert(x);
        onTap(t);
    }

    return (
        <g
            id={`${id}-g-axes`}
            width={width}
            height={height}
            onMouseLeave={onTouchExit}
            transform={`translate(${x}, ${y})`}>
            <YAxis
                id={`${id}-y-axis`}
                scale={yAxisScale}
                width={yAxisWidth}
                chartWidth={width}
                height={mainChartAreaHeight}
                padding={heightPadding}
            />
            {/* <BinLines
                id={`${id}-bin-lines`}
                transform={`translate(${yAxisWidth}, 0)`}
                width={width}
                height={mainChartAreaHeight}
                minTime={minTime}
                maxTime={maxTime}
                scale={zoomScale}
                binning={details}
                disabled={!timebar.mode}
            /> */}
            <rect
                key={`${id}-g-bg`}
                ref={TimebarBg}
                pointerEvents='all'
                onMouseMove={onBinTouchMove}
                onMouseDown={e => { onBrushFilterStart(e); onClickBg(e);}}
                onTouchStart={e => { onBrushFilterStart(e); onClickBg(e);}}
                onWheel={onBinWheel}
                width={width} height={height}
                style={{ fill: '#000', fillOpacity: 0 }}
                transform={`translate(${yAxisWidth}, 0)`}
            />
            <FilterBrushes
                id={`${id}-filters`}
                timebarRef={TimebarBg}
                clipPath={`url(#${clipPathId}-filters)`}
                width={width}
                height={mainChartAreaHeight}
                minTime={minTime}
                maxTime={maxTime}
                timeScale={zoomScale}
                timeDomain={zoomDomain}
                onMouseMove={onBinTouchMove}
                onTouchMove={onBinTouchMove}
                yAxisWidth={yAxisWidth + 10}
                filterBrushRanges={filterBrushRanges}
                transform={`translate(${yAxisWidth}, 0)`}
                onTap={onTap}
                onDoubleTap={onFilterBrushRemoved}
                onTimeChange={onFilterBrushTimeChange}
                showHandleArrow={timebar.mode}
            />
            <line key={`${id}-g-y-top-bar`} stroke="#C2C2C2" strokeWidth="2px"
                x1={yAxisWidth} x2={yAxisWidth + width}
                y1={0} y2={0} />
            <XAxis
                id={`${id}-x-axis`}
                x={yAxisWidth}
                y={mainChartAreaHeight}
                width={width}
                scale={zoomScale}
                height={xAxisHeight}
                padding={heightPadding}
                clipPath={`url(#${clipPathId}-filters)`}
            />
            <XAxisTitle
                x={yAxisWidth}
                y={mainChartAreaHeight + xAxisHeight}
                width={width}
                height={xAxisHeight}
                timeDomain={zoomDomain}
                detailsLevel={detailsLevel}
            />
            <Bins
                {...props}
                id={`${id}-g-bars`}
                timeScale={zoomScale}
                timeDomain={zoomDomain}
                clipPath={clipPathURL}
                transform={`translate(${yAxisWidth}, ${0})`}
                valueScale={yAxisScale}
                onTouchStart={onBrushFilterStart}
                onMouseMove={onBinTouchMove}
                onTouchMove={onBinTouchMove}
                onWheel={onBinWheel}
                width={width}
                height={mainChartAreaHeight}
                pointerEvents={onBinTouchMove ? 'all' : 'none'}
                padding={heightPadding}
                isWheelScrolling={isWheelScrolling}
                startBin={findBinIndex(+zoomDomain[0], binValues) - 1}
                endBin={findBinIndex(+zoomDomain[1], binValues) + 1}
            />
        </g>
    );
})) as React.ComponentClass<TimebarDetailsProps>;

export interface TimebarSummaryProps {
    [x: string]: any;
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    min: number;
    max: number;
    isPanZooming?: boolean;
    isPanScrolling?: boolean;
    yAxisWidth?: number;
    xAxisHeight?: number;
    timeScale: ScaleTime<number, number>;
    valueScale: ScaleLinear<number, number>;
    numBins: number;
    binValues: number[];
    minTime: number;
    maxTime: number;
    timeDomain: [number, number];
    zoomDomain: [number, number];
    onTouchExit?: (e: React.TouchEvent | React.MouseEvent) => void;
    onBrushTimeChange?: (v: {props: object, type: string, timeDomain: [number, number]}) => void;
}
const TimeBarSummary = withValueScale((props: TimebarSummaryProps) => {

    const {
        x, y, id, width, height, min, max,
        isPanZooming, isPanScrolling,
        yAxisWidth, xAxisHeight,
        valueScale, timeScale, numBins, binValues,
        minTime, maxTime, timeDomain, zoomDomain,
        onTouchExit, onBrushTimeChange,
    } = props;

    if (!valueScale) { return null; }

    const mainChartAreaHeight = Math.max(0, height - xAxisHeight);
    const yAxisScale = valueScale.range([mainChartAreaHeight, 0]);

    return (
        <g
            id={`${id}-g-axes`}
            width={width}
            height={height}
            onMouseLeave={onTouchExit}
            transform={`translate(${x}, ${y})`}>
            <Bins
                {...props}
                id={`${id}-g-bars`}
                transform={`translate(${yAxisWidth}, 0)`}
                valueScale={yAxisScale}
                width={width}
                height={mainChartAreaHeight}
                padding={0}
                pointerEvents={'none'}
                startBin={-1}
                endBin={numBins + 1}
                timeDomain={timeDomain}
            />
            <Brush
                id={`${id}-brush`}
                minTime={minTime}
                maxTime={maxTime}
                timeScale={timeScale}
                timeDomain={zoomDomain}
                isPanZooming={isPanZooming}
                isPanScrolling={isPanScrolling}
                onTimeChange={onBrushTimeChange}
                onTouchMove={onTouchExit}
                onMouseMove={onTouchExit}
                width={width}
                height={mainChartAreaHeight}
                mode={'negative'}
            />
            <XAxis
                id={`${id}-x-axis`}
                x={yAxisWidth}
                y={mainChartAreaHeight}
                width={width}
                height={xAxisHeight}
                scale={timeScale}
            />
        </g>
    );
}) as React.ComponentClass<TimebarSummaryProps>;

export interface FilterBrushesProps {
    width: number;
    height: number;
    yAxisWidth: number;
    id: string;
    clipPath: string;
    transform: string;
    timeScale: ScaleTime<number, number>;
    timeDomain: [number, number];
    minTime: number;
    maxTime: number;
    filterBrushRanges: number[],
    onMouseMove: (e: React.MouseEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTap: (t: number) => void;
    onDoubleTap: (v: {props: any}) => void;
    onTimeChange: (v: {props: any, type: string; timeDomain: [number, number] }) => void;
    showHandleArrow: boolean;
    timebarRef: React.MutableRefObject<any>;
}
function FilterBrushes(props: FilterBrushesProps) {
    const {
        width, height, yAxisWidth,
        id, clipPath, transform,
        timeScale, timeDomain,
        minTime, maxTime, filterBrushRanges = [],
        onMouseMove, onTouchMove, onTap, onDoubleTap, onTimeChange,
        showHandleArrow, timebarRef
    } = props;
    const endBrushIndex = findBinIndex(+timeDomain[1], filterBrushRanges) + 1;
    const startBrushIndex = findBinIndex(+timeDomain[0], filterBrushRanges) - 1;
    const brushesToRender = Math.max(0, endBrushIndex - startBrushIndex, Math.floor(filterBrushRanges.length / 2));
    const brushes = [];
    for (let i = -1; ++i < brushesToRender;) {
        const brushId = startBrushIndex + i;
        const t0 = filterBrushRanges[brushId * 2] || 0;
        const t1 = filterBrushRanges[brushId * 2 + 1] || 0;
        if (t1 < timeDomain[0] || t0 > timeDomain[1] || (t1 - t0) <= 0) {
            continue;
        }
        brushes.push(
            <Brush
                timebarRef={timebarRef}
                width={width}
                height={height}
                onTap={onTap}
                onDoubleTap={onDoubleTap}
                onTimeChange={onTimeChange}
                brushId={brushId}
                minTime={minTime}
                maxTime={maxTime}
                timeScale={timeScale}
                timeDomain={[t0, t1]}
                yAxisWidth={yAxisWidth}
                brushBgStyle={{ fillOpacity: 0.2 }}
                id={`${id}-brush-${brushId}`}
                key={`${id}-brush-${brushId}`}
                showHandleArrow={showHandleArrow}
            />
        );
    }
    return (
        <g id={id} clipPath={clipPath} transform={transform} onMouseMove={onMouseMove} onTouchMove={onTouchMove}>
            {brushes}
        </g>
    );
}

/**
 * Finds the bin which a given value falls under, otherwise finds the closest bin to that value. If the "getAdjacentBin"
 * argument is set, find the closest bin to the left or the right that is not the bin the value is currently
 * in if any. In this mode, if there is no valid adjacent bin in the direction specified, return the current bin.
 * @param value - Value to find the index of.
 * @param binValues - List of number pairs representing the bins assumed to be sorted from least to greatest.
 * Each pair consists of a bin start value and end value.
 * @param getAdjacentBin - Value which determines if the function operates in find bin mode or find adjacent
 * bin mode where the bin index found cannot contain the value provided. Only three values are valid: 0 for find bin mode,
 * -1 for find adjacent bin to the left (smaller than provided value), and 1 for find adjacent bin to the right (larger than
 * the provided value).
 */
export function findBinIndex(value: number, binValues: number[] = [], getAdjacentBin: 0 | -1 | 1 = 0) {
    const maxIndex = binValues.length * 0.5 - 1;

    // Check for easy special cases
    if(!binValues.length || (value <= binValues[0] && getAdjacentBin !== 1)) {
        return 0;
    }
    if(value >= binValues[binValues.length - 1] && getAdjacentBin !== -1) {
        return maxIndex;
    }

    // Use binary search to find the bin index
    let left = 0, right = maxIndex;
    while(left <= right) {
        const mid = Math.floor((left + right) * 0.5);
        // Check if the value is within the bin's range
        if (binValues[mid * 2] <= value && value < binValues[2 * mid + 1]) {
            return Math.max(0, Math.min(maxIndex, mid + getAdjacentBin));
        } else if (binValues[mid * 2] > value) {
            right = mid - 1;
        } else {
            left = mid + 1;
        }
    }

    // Value is not within any bin
    // If adjacent bin - find the nearest bin forward or backward
    if(getAdjacentBin) {
        return getAdjacentBin === 1 ? left : right;
    }
    // Get the bin closest to the value regardless of direction
    return binValues[left * 2] - value <= value - binValues[right * 2 + 1] ? left : right;
}

export function getAdjacentBin({ details, timebar }: { details: BinDataContainer, timebar: Timebar }, isBackward = false) {
    const { binValues } = details.filtered;
    const { playheadStart, interval } = timebar;

    const nextIndex = binValues.findIndex((v, i) => !(i % 2) && v === playheadStart) + (isBackward ? -2 : 2);

    return (interval?.length
            ? getAdjacentBinWithIntervals
            : getAdjacentBinNoIntervals
        )({ details, timebar }, nextIndex, isBackward);
}

function getAdjacentBinWithIntervals({ details, timebar }: { details: BinDataContainer, timebar: Timebar }, nextIndex: number, isBackward: boolean) {
    const { binValues } = details.filtered;
    const { loop, playheadStart, interval } = timebar;

    // playhead in interval
    for(let i = 0; i < interval.length; i += 2) {
        if(playheadStart >= interval[i] && playheadStart < interval[i+1]) {
            const intervalStartIndex = findBinIndex(interval[i], binValues, 1) * 2;
            const intervalEndIndex = findBinIndex(interval[i+1], binValues, -1) * 2;
            if(isBackward) { // ignore loop on backward
                return nextIndex < intervalStartIndex ? playheadStart : binValues[nextIndex];
            } else {
                return nextIndex > intervalEndIndex
                    ? (loop ? binValues[intervalStartIndex] : playheadStart)
                    : binValues[nextIndex];
            }
        }
    }

    // playhead not in any interval
    const hasIntervalAhead = interval.some(v => isBackward ? v <= playheadStart : v >= playheadStart);
    const reference = hasIntervalAhead ? playheadStart : (isBackward ? binValues[binValues.length - 1] : binValues[0]);

    let intervalStart = null;
    let intervalEnd = null;
    for(let i = 0; i < interval.length; i += 2) {
        if(isBackward) {
            if(reference - interval[i] >= 0) {
                if((intervalStart === null && intervalEnd === null) || (reference - interval[i+1]) < (reference - intervalEnd)) {
                    intervalStart = interval[i];
                    intervalEnd = interval[i+1];
                }
            }
        } else {
            if(interval[i + 1] - reference >= 0) {
                if((intervalStart === null && intervalEnd === null) || (interval[i] - reference) < (intervalStart - reference)) {
                    intervalStart = interval[i];
                    intervalEnd = interval[i+1];
                }
            }
        }
    }
    return binValues[
        (isBackward ? findBinIndex(intervalEnd, binValues, -1) : findBinIndex(intervalStart, binValues, 1)) * 2
    ];
}

function getAdjacentBinNoIntervals({ details, timebar }: { details: BinDataContainer, timebar: Timebar }, nextIndex: number, isBackward: boolean) {
    const { binValues } = details.filtered;
    const { loop, playheadStart } = timebar;

    if(isBackward) { // ignore loop on backward
        return nextIndex < 0 ? playheadStart : binValues[nextIndex];
    } else {
        return nextIndex > binValues.length - 1
            ? (loop ? binValues[0] : playheadStart)
            : binValues[nextIndex];
    }
}